import { useCallback, useState } from 'react';
import { isTimeWindowEqual, type TimeWindow } from '../utils';

interface UseBlockModalArgs {
    changeTemplate: (updater: (currentBlocks: TimeWindow[]) => TimeWindow[]) => void;
    setCurrentBlock: (block?: TimeWindow | null) => void;
}

interface UseBlockModalReturn {
    showModal: boolean;
    updateBlock: (oldSettings: TimeWindow, newSettings: TimeWindow) => void;
    deleteBlock: (block: TimeWindow) => void;
    openEditModal: (block: TimeWindow) => void;
    closeModal: () => void;
}

export function useBlockModal({
    changeTemplate,
    setCurrentBlock,
}: UseBlockModalArgs): UseBlockModalReturn {
    const [showModal, setShowModal] = useState(false);

    const updateBlock = useCallback(
        (oldSettings: TimeWindow, newSettings: TimeWindow) => {
            changeTemplate((currentBlocks) => {
                const nextBlocks = [...currentBlocks];
                const index = nextBlocks.findIndex((schedule) =>
                    isTimeWindowEqual(schedule, oldSettings)
                );
                nextBlocks[index] = newSettings;
                return nextBlocks;
            });
            setCurrentBlock();
        },
        [changeTemplate, setCurrentBlock]
    );

    const deleteBlock = useCallback(
        (block: TimeWindow) => {
            changeTemplate((currentBlocks) =>
                currentBlocks.filter((schedule) => !isTimeWindowEqual(schedule, block))
            );
            setCurrentBlock();
        },
        [changeTemplate, setCurrentBlock]
    );

    const openEditModal = useCallback(
        (block: TimeWindow) => {
            setCurrentBlock(block);
            setShowModal(true);
        },
        [setCurrentBlock]
    );

    const closeModal = useCallback(() => {
        setShowModal(false);
        setCurrentBlock();
    }, [setCurrentBlock]);

    return {
        showModal,
        updateBlock,
        deleteBlock,
        openEditModal,
        closeModal,
    };
}
